import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { TolgeeWrapper } from './components/tolgee-wrapper';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { PaymentProvider } from './context/PaymentContext';
import ErrorBoundary from './components/ErrorBoundary';
import trackingManager from './lib/tracking';
import { useEffect } from 'react';

function App() {
  // Obtenemos el lenguaje del localStorage o usamos 'es' por defecto
  const locale = typeof window !== 'undefined' ?
    (localStorage.getItem('tolgee-language') || 'es') : 'es';

  // Solo mostrar logs en desarrollo
  if (import.meta.env.DEV) {
    console.log('🔧 [App] Iniciando aplicación con locale:', locale);
    console.log('🔧 [App] Entorno:', typeof window !== 'undefined' ? 'Browser' : 'Server');
  }
  
  // ASCII Art de Jarad con colores múltiples
  const renderJaradLogo = () => {
    // Caracteres para líneas sólidas y espacios
    const solidChars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ123456789+{}<>;';
    const spaceChars = " `.,_-'";
    const chars = [spaceChars, solidChars];
    
    // Colores de Jarad: Amarillo, Rojo, Rojito
    const colors = ['#E0E0E0', '#f6cf02', '#e53e3e', '#c53030'];
    
    // Mapeo preciso del ASCII art de Jarad
    // [cantidad, tipo_char, color_index]
    const map = [
      // Línea 1: "       ▓▓▓                    "
      [7, 0, 0], [3, 1, 1], [20, 0, 0], null,
      
      // Línea 2: "       ▓░▒▓        ▓▓▓▓▓▓ ▓▓▓ "
      [7, 0, 0], [1, 1, 1], [1, 1, 2], [1, 1, 1], [8, 0, 0], [6, 1, 1], [1, 1, 1], [5, 1, 1], null,
      
      // Línea 3: "     ▓▓▒▒░░▓▓     ▓▒▒▒▒▒▓▓▓▒▓ "
      [5, 0, 0], [2, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 1], [5, 0, 0], [5, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], [1, 1, 1], null,
      
      // Línea 4: "     ▓▓░░░░░▒▓ ▓▒▒░░░░░░░▒▒▒▓ "
      [5, 0, 0], [2, 1, 1], [5, 1, 2], [1, 1, 1], [2, 1, 2], [1, 1, 1], [2, 1, 2], [5, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], null,
      
      // Línea 5: "      ▓▓░░░░░▒▓▒░░░░░▒▒░░░▒▒▓ "
      [6, 0, 0], [2, 1, 1], [5, 1, 2], [1, 1, 1], [2, 1, 2], [1, 1, 1], [5, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], null,
      
      // Línea 6: "      ▓▒░░░░░▒▓▒░░░░▒▒▓▒░▒▒▓▓ "
      [6, 0, 0], [1, 1, 1], [5, 1, 2], [1, 1, 1], [2, 1, 2], [1, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [1, 1, 1], [2, 1, 2], [2, 1, 1], null,
      
      // Línea 7: "       ▓▒░░░░▒▓▓▒░░░░░░▒▒▒▒▓▓ "
      [7, 0, 0], [1, 1, 1], [5, 1, 2], [2, 1, 1], [2, 1, 2], [1, 1, 1], [5, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], null,
      
      // Línea 8: "       ▓▒░░░░░░░▒░░░░░░░░░▓▓  "
      [7, 0, 0], [1, 1, 1], [5, 1, 2], [7, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], null,
      
      // Línea 9: "        ▓▒░░░░░░░░░░░░░▒▓▓    "
      [8, 0, 0], [1, 1, 1], [5, 1, 2], [7, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], [4, 0, 0], null,
      
      // Línea 10: "  ▓▓▓▓▓▒░▒░░░░░░░░░░░░░▒▓     "
      [2, 0, 0], [5, 1, 1], [1, 1, 1], [1, 1, 2], [1, 1, 1], [7, 1, 2], [7, 1, 1], [2, 1, 2], [2, 1, 1], [5, 0, 0], null,
      
      // Línea 11: " ▓▓▒▒▒░░░▒▒░░░░▒░░░░░░▓▓      "
      [1, 0, 0], [5, 1, 1], [5, 1, 2], [1, 1, 1], [3, 1, 2], [2, 1, 1], [5, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], [6, 0, 0], null,
      
      // Línea 12: "  ▓▒▒▒░░▓▓▓▓░▒▒▒▒▒░▒▓▒▓       "
      [2, 0, 0], [5, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [1, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], [7, 0, 0], null,
      
      // Línea 13: "   ▓▒░▓▓     ▓▓▓▓▓▓▓▓▒▓       "
      [3, 0, 0], [2, 1, 1], [2, 1, 2], [2, 1, 1], [5, 0, 0], [6, 1, 1], [2, 1, 2], [2, 1, 1], [2, 1, 2], [2, 1, 1], [7, 0, 0], null,
      
      // Línea 14: "    ▓▓                        "
      [4, 0, 0], [2, 1, 1], [24, 0, 0], null
    ];
    
    // Generar el string ASCII con marcadores de color
    const ascii = map.reduce((agg, val) => {
      if (val === null) return agg + '\n';
      agg += '%c';
      let characters = chars[val[1]].split('');
      const randChar = () => characters[Math.floor(Math.random() * characters.length)];
      for (let i = 0; i < val[0]; i++) {
        agg += randChar();
      }
      return agg;
    }, '');
    
    // Generar los estilos CSS para cada segmento
    const colorStrings = map
      .filter(x => x !== null)
      .map(val => `color: ${colors[val[2]]}; font-family: monospace; font-weight: bold; font-size: 12px;`);
    
    // Solo mostrar logo en desarrollo
    if (import.meta.env.DEV) {
      console.log(ascii, ...colorStrings);
    }
  };

  // Renderizar el logo de Jarad solo en desarrollo
  if (import.meta.env.DEV) {
    renderJaradLogo();
  }
    
  // Inicializar el tracking al cargar la aplicación
  useEffect(() => {
    const initializeTracking = async () => {
      try {
        await trackingManager.initialize();
        if (import.meta.env.DEV) {
          console.log('Tracking initialized successfully');
        }
      } catch (error) {
        // Siempre mostrar errores críticos
        console.error('Failed to initialize tracking:', error);
      }
    };

    initializeTracking();
    // Registrar service worker PWA

  }, []);

  if (import.meta.env.DEV) {
    console.log('🔧 [App] Renderizando TolgeeWrapper');
  }

  return (
    <ErrorBoundary>
      <TolgeeWrapper locale={locale}>
        <AuthProvider>
          <PaymentProvider>
            <CartProvider>
              {router && <RouterProvider router={router} />}
            </CartProvider>
          </PaymentProvider>
        </AuthProvider>
      </TolgeeWrapper>
    </ErrorBoundary>
  );
}

export default App;
